import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { ApiPlatform } from '@/api/types';
import { AssetIcon } from '@/blocks/components/AssetIcon';
import { BaseAutocomplete } from '@/blocks/components/Controls/Autocomplete';
import { AutocompleteOption } from '@/blocks/components/Controls/Autocomplete/Popper/AutocompleteOption';
import { SearchStartAdornment } from '@/blocks/components/Controls/Autocomplete/SearchStartAdornment';
import { assetSearchCallback } from '@/blocks/components/Controls/Autocomplete/helpers';
import { TextField } from '@/blocks/components/Controls/TextField';
import { WithClassName } from '@/blocks/components/types';
import { FormatAssetName, extractAssetName } from '@/blocks/formatters/format-asset-name';

import { toRecapCategory } from '@/app/[lang]/price-chart/[category]/data';

type AssetSearchProps = {
  platform?: ApiPlatform;
  onChange: (...args: any[]) => any;
  options?: any[];
} & WithClassName;

/*
delete after release/139-140
// src/app/[lang]/price-chart/[category]/PriceChartTableAndControlsSection/index.tsx
pages.priceCharts.root.sections.markets.search.loading
pages.priceCharts.root.sections.markets.search.noResults
pages.priceCharts.root.sections.markets.search.label
pages.priceCharts.root.sections.markets.search.placeholder
// src/app/[lang]/crypto-futures/(pages)/markets/CryptoFuturesMarketsTableSection/index.tsx
pages.cryptoFuturesMarkets.sections.markets.searchInput.loadingText
pages.cryptoFuturesMarkets.sections.markets.searchInput.noResultsText
pages.cryptoFuturesMarkets.sections.markets.searchInput.label
pages.cryptoFuturesMarkets.sections.markets.searchInput.placeholder
// src/app/[lang]/crypto-futures/(pages)/fees/sections/CryptoFuturesFeesAllMarketsTable/CryptoFuturesFeesAllMarketsSearch/index.tsx
pages.cryptoFuturesMarkets.sections.markets.searchInput.loadingText
pages.cryptoFuturesMarkets.sections.markets.searchInput.noResultsText
pages.cryptoFuturesMarkets.sections.markets.searchInput.label
pages.cryptoFuturesMarkets.sections.markets.searchInput.placeholder
// src/app/[lang]/converters/sections/ConvertersTableSection/index.tsx
pages.converters.index.sections.table.searchInput.loadingText
pages.converters.index.sections.table.searchInput.noResultsText
pages.converters.index.sections.table.searchInput.label
pages.converters.index.sections.table.searchInput.placeholder
// src/app/[lang]/cfd-trading/(pages)/fees/sections/CfdFeesAllMarketsTable/CfdFeesAllMarketsSearch/index.tsx
pages.cfd.all.sections.markets.searchInput.loadingText
pages.cfd.all.sections.markets.searchInput.noResultsText
pages.cfd.all.sections.markets.searchInput.label
pages.cfd.all.sections.markets.searchInput.placeholder
// src/app/[lang]/cfd-trading/(pages)/(markets)/markets/CfdAllMarketsTable/CfdAllMarketsSearch/index.tsx
pages.cfd.all.sections.markets.searchInput.loadingText
pages.cfd.all.sections.markets.searchInput.noResultsText
pages.cfd.all.sections.markets.searchInput.label
pages.cfd.all.sections.markets.searchInput.placeholder

pages.converters.index.sections.table.searchInput.loadingText
pages.converters.index.sections.table.searchInput.noResultsText
pages.converters.index.sections.table.searchInput.label
pages.converters.index.sections.table.searchInput.placeholder
 */

export function AssetSearch({
  platform = ApiPlatform.DEVEX,
  onChange,
  className,
  options,
}: AssetSearchProps) {
  const { markets, loading, error } = useRecap(platform);

  const tr = useTranslations();

  if (!markets) {
    return null;
  }

  return (
    <BaseAutocomplete
      loading={loading}
      options={options || markets}
      onChange={onChange}
      className={classNames(className)}
      getOptionLabel={(option: RecapAsset) => option.name}
      isOptionEqualToValue={(option: RecapAsset, value: RecapAsset) => option.name === value.name}
      filterOptions={assetSearchCallback}
      renderInput={(params) => (
        <TextField
          {...params}
          label={tr('components.autocomplete.assets.label')}
          placeholder={tr('components.autocomplete.assets.placeholder')}
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchStartAdornment />,
          }}
        />
      )}
      renderOption={(props, option) => (
        <AutocompleteOption
          optionProps={props}
          icon={<AssetIcon assetName={option.name} loading={'eager'} key={'assetIcon'} />}
          title={
            <FormatAssetName
              assetName={extractAssetName({
                name: option.name,
                description: option.description,
                category: option.category,
              })}
            />
          }
          key={option.name}
          description={<FormatAssetName assetName={option.name} />}
          label={tr(
            `components.autocomplete.assets.option.assetCategory.${toRecapCategory(option.category)}`,
          )}
        />
      )}
    />
  );
}

// @ts-ignore
export { search as assetSearchCentered } from './styles.module.scss';
